import styles from './Chart.module.css'

// ── Bar Chart ───────────────────────────────────────────────────────────────

export interface BarChartProps {
  data: { label: string; value: number; color?: string }[]
  height?: number
  showValues?: boolean
  label?: string
}

export function BarChart({ data, height = 160, showValues = true, label }: BarChartProps) {
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div className={styles.chart} aria-label={label ?? 'Bar chart'} role="img">
      <div className={styles.barGroup} style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className={styles.barCol}>
            {showValues && (
              <span className={styles.barValue}>{d.value}</span>
            )}
            <div
              className={styles.bar}
              style={{
                height: `${(d.value / max) * 100}%`,
                background: d.color ?? 'var(--accent-primary)',
              }}
              role="presentation"
            />
            <span className={styles.barLabel}>{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Line Chart ──────────────────────────────────────────────────────────────

export interface LineChartProps {
  data: { label: string; value: number }[]
  width?: number
  height?: number
  color?: string
  showDots?: boolean
  label?: string
}

export function LineChart({
  data, width = 320, height = 120, color = 'var(--accent-primary)', showDots = true, label,
}: LineChartProps) {
  if (data.length < 2) return null
  const values = data.map(d => d.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const pad = 24

  const pts = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (width - pad * 2),
    y: pad + (1 - (d.value - min) / range) * (height - pad * 2),
    label: d.label,
    value: d.value,
  }))

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

  return (
    <div className={styles.chart}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
        aria-label={label ?? 'Line chart'} role="img">
        {/* Baseline */}
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
          stroke="var(--border-subtle)" strokeWidth="1" />
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Area fill */}
        <path d={`${pathD} L${pts[pts.length-1].x},${height-pad} L${pts[0].x},${height-pad} Z`}
          fill={color} opacity=".08" />
        {showDots && pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={color}>
            <title>{p.label}: {p.value}</title>
          </circle>
        ))}
        {/* X-axis labels */}
        {pts.map((p, i) => (
          <text key={i} x={p.x} y={height - 6} textAnchor="middle"
            style={{ fontSize: 9, fill: 'var(--text-muted)', fontFamily: 'inherit' }}>
            {p.label}
          </text>
        ))}
      </svg>
    </div>
  )
}

// ── Donut Chart ─────────────────────────────────────────────────────────────

export interface DonutChartProps {
  segments: { label: string; value: number; color: string }[]
  size?: number
  thickness?: number
  centerLabel?: string
  centerValue?: string
}

export function DonutChart({
  segments, size = 120, thickness = 22, centerLabel, centerValue,
}: DonutChartProps) {
  const r = (size - thickness) / 2
  const cx = size / 2
  const circ = 2 * Math.PI * r
  const total = segments.reduce((s, d) => s + d.value, 0) || 1

  let offset = 0
  const arcs = segments.map(seg => {
    const pct = seg.value / total
    const dash = pct * circ
    const arc = { ...seg, dash, gap: circ - dash, offset }
    offset += dash
    return arc
  })

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      aria-label="Donut chart" role="img" style={{ display: 'block' }}>
      {/* Background ring */}
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--bg-elevated)" strokeWidth={thickness} />
      {arcs.map((arc, i) => (
        <circle key={i} cx={cx} cy={cx} r={r}
          fill="none" stroke={arc.color} strokeWidth={thickness}
          strokeDasharray={`${arc.dash} ${arc.gap}`}
          strokeDashoffset={-arc.offset}
          style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }}>
          <title>{arc.label}: {arc.value} ({Math.round((arc.value/total)*100)}%)</title>
        </circle>
      ))}
      {centerValue && (
        <>
          <text x={cx} y={cx - 4} textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 18, fontWeight: 700, fill: 'var(--text-primary)', fontFamily: 'inherit' }}>
            {centerValue}
          </text>
          {centerLabel && (
            <text x={cx} y={cx + 14} textAnchor="middle"
              style={{ fontSize: 9, fill: 'var(--text-muted)', fontFamily: 'inherit' }}>
              {centerLabel}
            </text>
          )}
        </>
      )}
    </svg>
  )
}
